import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/zera/Navbar';
import PageHero from '@/components/zera/PageHero';
import Footer from '@/components/zera/Footer';
import { Users, Ruler, Fuel, ArrowRight, X, MapPin } from 'lucide-react';


type Destination = { name: string; image: string };

const DEST = {
  sarande: { name: 'Sarandë', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887451818_5b1d2596.jpg' },
  ksamil: { name: 'Ksamil', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887491619_2f13a5f3.png' },
  himare: { name: 'Himarë', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887604746_c2a9d815.png' },
  corfu: { name: 'Corfu', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887623669_16c6c4e2.jpg' },
  gjipe: { name: 'Gjipe Beach', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887642847_ff854ec1.jpg' },
  dhermi: { name: 'Dhërmi', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887660572_21121426.jpg' },
  portoPalermo: { name: 'Porto Palermo', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887677677_9f6950b8.jpg' },
  karaburun: { name: 'Karaburun Peninsula', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887742794_393d6626.png' },
  sazan: { name: 'Sazan Island', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887762028_0abf46c9.jpg' },
  vlore: { name: 'Vlorë', image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776887777473_439a277a.jpg' },
} satisfies Record<string, Destination>;

const yachts = [
  {
    name: 'Adriatic Pearl',
    type: 'Motor Yacht',
    length: '28m / 92ft',
    guests: 8,
    cabins: 4,
    crew: 4,
    destinations: [DEST.sarande, DEST.ksamil, DEST.himare, DEST.portoPalermo],
    features: ['Flybridge', 'Jacuzzi', 'Water Toys', 'Stabilizers'],
  },
  {
    name: 'Ionian Dream',
    type: 'Sailing Yacht',
    length: '22m / 72ft',
    guests: 6,
    cabins: 3,
    crew: 3,
    destinations: [DEST.ksamil, DEST.corfu, DEST.sarande],
    features: ['Sail Power', 'Snorkeling Gear', 'Tender', 'Sun Deck'],
  },
  {
    name: 'Riviera Majesty',
    type: 'Super Yacht',
    length: '42m / 138ft',
    guests: 12,
    cabins: 6,
    crew: 8,
    destinations: [DEST.gjipe, DEST.dhermi, DEST.himare, DEST.vlore],
    features: ['Helipad', 'Cinema', 'Spa', 'Jet Skis', 'Beach Club'],
  },
  {
    name: 'Albanian Star',
    type: 'Catamaran',
    length: '18m / 59ft',
    guests: 8,
    cabins: 4,
    crew: 2,
    destinations: [DEST.karaburun, DEST.sazan, DEST.vlore],
    features: ['Spacious Deck', 'BBQ', 'Kayaks', 'Snorkeling'],
  },
];

const routes = [
  { name: 'Riviera Explorer', duration: '7 Days', stops: 'Sarandë → Ksamil → Himarë → Porto Palermo → Vlorë', highlight: 'The complete Albanian Riviera experience' },
  { name: 'Island Hopper', duration: '3 Days', stops: 'Sarandë → Ksamil Islands → Corfu → Sarandë', highlight: 'Cross-border Mediterranean adventure' },
  { name: 'Hidden Coves', duration: '5 Days', stops: 'Vlorë → Gjipe Beach → Dhërmi → Jale → Himarë', highlight: 'Discover beaches only accessible by sea' },
  { name: 'Sunset Coast', duration: '2 Days', stops: 'Durrës → Karaburun Peninsula → Sazan Island', highlight: 'Albania\'s secret military island and pristine peninsula' },
];

const YachtCharters: React.FC = () => {
  const navigate = useNavigate();
  const [selectedYacht, setSelectedYacht] = useState<typeof yachts[0] | null>(null);

  const openYacht = (y: typeof yachts[0]) => {
    setSelectedYacht(y);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237647164_03f0a4b0.png"
        title={"Yacht\nCharters"}
        subtitle="Cruise the Albanian Riviera aboard a luxury vessel — discovering hidden coves, pristine beaches, and ancient coastal towns."
        badge="Maritime Collection"
        height="h-screen"
      />

      {/* Fleet */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Our Fleet</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light mt-4">Choose Your Vessel</h2>
            <p className="font-sans text-sm text-[#666] mt-4 max-w-xl mx-auto">Each vessel journeys through its own signature collection of Albanian Riviera destinations.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {yachts.map((yacht) => (
              <div key={yacht.name} className="group bg-white border border-[#E8DFD0] hover:border-[#C9A961]/30 transition-all duration-500 overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0D1B2A]">
                  <img
                    src={yacht.destinations[0].image}
                    alt={`${yacht.name} — ${yacht.destinations[0].name}`}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                    <MapPin className="w-4 h-4 text-[#C9A961]" />
                    <span className="font-serif text-lg font-light drop-shadow">{yacht.destinations[0].name}</span>
                  </div>
                </div>

                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-serif text-xl text-[#1A3B5C] font-light">{yacht.name}</h3>
                      <span className="font-sans text-xs text-[#999]">{yacht.length}</span>
                    </div>
                    <button
                      onClick={() => navigate('/booking')}
                      className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                    >
                      Book Now
                    </button>
                  </div>

                  <div className="flex items-center gap-6 mb-4 text-[#666]">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs">{yacht.guests} Guests</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Ruler className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs">{yacht.cabins} Cabins</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Fuel className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-sans text-xs">{yacht.crew} Crew</span>
                    </div>
                  </div>

                  {/* Itinerary chips */}
                  <div className="mb-4">
                    <span className="font-sans text-[9px] tracking-[0.2em] uppercase text-[#C9A961]">Destinations</span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                      {yacht.destinations.map((d, i) => (
                        <React.Fragment key={d.name}>
                          <span className="font-serif text-sm text-[#1A3B5C]">{d.name}</span>
                          {i < yacht.destinations.length - 1 && <span className="text-[#C9A961]">→</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {yacht.features.map((f) => (
                      <span key={f} className="font-sans text-[9px] tracking-wider uppercase px-3 py-1 bg-[#F5F1E8] text-[#999]">{f}</span>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => openYacht(yacht)}
                      className="flex-1 font-sans text-[10px] tracking-[0.2em] uppercase py-3 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-all text-center"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => navigate('/booking')}
                      className="flex-1 font-sans text-[10px] tracking-[0.2em] uppercase py-3 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all text-center"
                    >
                      Reserve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Routes */}
      <section className="py-20 lg:py-32 bg-[#0D1B2A]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Curated Itineraries</span>
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mt-4">Signature Routes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {routes.map((route) => (
              <div key={route.name} className="border border-white/10 p-8 hover:border-[#C9A961]/30 transition-all duration-500">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-xl text-white font-light">{route.name}</h3>
                  <span className="font-sans text-[10px] tracking-wider text-[#C9A961] uppercase">{route.duration}</span>
                </div>
                <p className="font-sans text-xs text-white/40 mb-3">{route.stops}</p>
                <p className="font-sans text-sm text-white/60 mb-6">{route.highlight}</p>
                <button onClick={() => navigate('/booking')} className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] flex items-center gap-2 hover:gap-3 transition-all">
                  Book This Route <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Yacht Detail Modal */}
      {selectedYacht && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelectedYacht(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-[#0D1B2A]">
              <img
                src={selectedYacht.destinations[0].image}
                alt={selectedYacht.destinations[0].name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                <MapPin className="w-4 h-4 text-[#C9A961]" />
                <span className="font-serif text-xl font-light drop-shadow">{selectedYacht.destinations[0].name}</span>
              </div>

              <button onClick={() => setSelectedYacht(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
                <X className="w-5 h-5 text-[#1A3B5C]" />
              </button>
            </div>


            <div className="p-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">Luxury Charter</span>
                  <h2 className="font-serif text-3xl text-[#1A3B5C] font-light mt-1">{selectedYacht.name}</h2>
                  <p className="font-sans text-xs text-[#999] mt-1">{selectedYacht.length}</p>
                </div>
                <button
                  onClick={() => { setSelectedYacht(null); navigate('/booking'); }}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                >
                  Book Now
                </button>
              </div>

              {/* Full itinerary */}
              <div className="mb-8">
                <h4 className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] mb-3">Itinerary</h4>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {selectedYacht.destinations.map((d, i) => (
                    <React.Fragment key={d.name}>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-[#C9A961]" />
                        <span className="font-serif text-base text-[#1A3B5C]">{d.name}</span>
                      </div>
                      {i < selectedYacht.destinations.length - 1 && <ArrowRight className="w-4 h-4 text-[#C9A961]" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedYacht.guests}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Guests</p>
                </div>
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedYacht.cabins}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Cabins</p>
                </div>
                <div className="text-center p-4 bg-[#F5F1E8]">
                  <span className="font-serif text-2xl text-[#1A3B5C]">{selectedYacht.crew}</span>
                  <p className="font-sans text-[9px] tracking-wider uppercase text-[#999] mt-1">Crew</p>
                </div>
              </div>
              <h4 className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] mb-3">Amenities & Features</h4>
              <div className="flex flex-wrap gap-2 mb-8">
                {selectedYacht.features.map((f) => (
                  <span key={f} className="font-sans text-xs px-4 py-2 bg-[#F5F1E8] text-[#666]">{f}</span>
                ))}
              </div>
              <button onClick={() => { setSelectedYacht(null); navigate('/booking'); }} className="w-full font-sans text-[11px] tracking-[0.25em] uppercase py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all">
                Reserve This Yacht
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default YachtCharters;
